import { jsxs, jsx } from 'react/jsx-runtime';
import { useMemo } from 'react';
import classNames from 'classnames';
import { Button } from '../../Button/Button.es.js';
import { Icon } from '../../Icon/Icon.es.js';
import { TextLink } from '../../TextLink/TextLink.es.js';

const FileUploaderItem = ({ fileName, fileSize, useSIUnits = true, decimalPlaces = 1, loading = false, disabled = false, variant = 'success', cancelText = 'Cancel', deleteText = 'Delete', className, onActionClick, noCancel = false, onFileNameClick, ...props })=>{
    /**
     * Convert a file size in bytes into a human-readable format.
     * @returns The file size in a human-readable string format, including the appropriate number of decimal places and the correct unit.
     */ const humanReadableFileSize = useMemo(()=>{
        const threshold = useSIUnits ? 1000 : 1024;
        const units = useSIUnits ? [
            'kB',
            'MB',
            'GB',
            'TB',
            'PB',
            'EB',
            'ZB',
            'YB'
        ] : [
            'KiB',
            'MiB',
            'GiB',
            'TiB',
            'PiB',
            'EiB',
            'ZiB',
            'YikB'
        ];
        if (Math.abs(fileSize) < threshold) {
            return `${fileSize} B`;
        }
        const unitIndex = Math.floor(Math.log(Math.abs(fileSize)) / Math.log(threshold));
        const unit = units[unitIndex - 1];
        const readableSize = (fileSize / Math.pow(threshold, unitIndex)).toFixed(decimalPlaces);
        return `${readableSize} ${unit}`;
    }, [
        decimalPlaces,
        fileSize,
        useSIUnits
    ]);
    const actionButtonText = useMemo(()=>{
        if (loading) {
            return cancelText;
        }
        return deleteText;
    }, [
        cancelText,
        deleteText,
        loading
    ]);
    const actionButtonHidden = useMemo(()=>{
        return loading && noCancel;
    }, [
        loading,
        noCancel
    ]);
    // TODO: Revisit renaming onFileNameClick and onActionClick
    const fileNameClickable = useMemo(()=>!disabled && !loading && onFileNameClick != null, [
        disabled,
        loading,
        onFileNameClick
    ]);
    const iconName = useMemo(()=>{
        if (loading) {
            return 'CircleSpinnerSolid';
        }
        switch(variant){
            case 'error':
                return 'ExclamationCircleSolid';
            case 'success':
                return 'CheckCircleSolid';
        }
    }, [
        loading,
        variant
    ]);
    return jsxs("div", {
        ...props,
        className: classNames('cb-file-uploader-item', {
            disabled
        }, className),
        children: [
            jsx("div", {
                className: "icon",
                children: jsx(Icon, {
                    className: classNames({
                        loading,
                        check: !loading && variant === 'success',
                        error: !loading && variant === 'error'
                    }),
                    name: iconName,
                    size: 24
                })
            }),
            jsxs("div", {
                className: "file-info",
                children: [
                    jsxs("div", {
                        className: "file-name",
                        children: [
                            fileNameClickable && jsx(TextLink, {
                                onClick: onFileNameClick,
                                type: "button",
                                variant: "primary",
                                children: fileName
                            }),
                            !fileNameClickable && fileName
                        ]
                    }),
                    jsx("div", {
                        className: "file-size",
                        children: humanReadableFileSize
                    })
                ]
            }),
            !actionButtonHidden && onActionClick && jsxs(Button, {
                className: "action-button",
                disabled: disabled,
                onClick: onActionClick,
                variant: "text",
                children: [
                    !loading && jsx(Icon, {
                        className: "icon",
                        name: "TrashOutline",
                        size: 18
                    }),
                    actionButtonText
                ]
            })
        ]
    });
};

export { FileUploaderItem };
//# sourceMappingURL=FileUploader.Item.es.js.map
